import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '../common/Divider';
import Dropdown from '../forecast/Dropdown';
import { SearchInput } from '../common/SearchInput';
import SubmitButton from '../common/SubmitButton';
import SVGImage from '../layout/SVGImage';
import { FilledInput } from '../common/FilledInput';
import { CancleButton } from '../common/CancleButton';

interface OperationManagementResourceRegistrationModalProps {
  testId?: string;
  className?: string;
  onClick?: () => void;
}

const testData = [
  { siteId: '1', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '2', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '3', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '4', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '5', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '6', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '7', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '8', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '9', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '10', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
  { siteId: '11', siteName: '(주)동남코리아 태양광 발전소 (2404-001)' },
];

enum ResourceRegistrationProgress {
  AssociatedPlant,
  ResourceRegistration,
}

export const OperationManagementResourceRegistrationModal: React.FC<
  OperationManagementResourceRegistrationModalProps
> = ({ testId, className, onClick }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(
    ResourceRegistrationProgress.AssociatedPlant
  );
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<Set<string>>(new Set());

  const handleChange = (text: string) => {
    setSearchKeyword(text);
  };

  const handleSiteClick = (id: string, cancle: boolean = false) => {
    const copiedSet = new Set(selectedSite);
    if (cancle) {
      copiedSet.delete(id);
    } else {
      copiedSet.add(id);
    }
    setSelectedSite(copiedSet);
  };

  const filteredData = useMemo(() => {
    return testData.filter((data) => data.siteName.includes(searchKeyword));
  }, [testData, searchKeyword]);

  const selectedData = useMemo(() => {
    return testData.filter((data) => selectedSite.has(data.siteId));
  }, [testData, selectedSite]);

  return (
    <div
      className={`flex flex-col items-end ${className}`}
      data-testid={testId}
    >
      <SVGImage
        className="mb-[15px] cursor-pointer"
        testId={`${testId}-close-button`}
        name="cancle"
        onClick={onClick}
      />
      <div className={`w-full bg-white p-10 pt-9 ${className}`}>
        {progress === ResourceRegistrationProgress.AssociatedPlant && (
          <p
            data-testid={`${testId}-title`}
            className="noto-semibold-4 leading-[23px] text-dark-3"
          >
            {t('management.resource.registerResourceModalTitle', {
              context: 'associatedPlant',
            })}
          </p>
        )}
        {progress === ResourceRegistrationProgress.ResourceRegistration && (
          <p
            data-testid={`${testId}-title`}
            className="noto-semibold-4 leading-[23px] text-dark-3"
          >
            {t('management.resource.registerResourceModalTitle', {
              context: 'resourceRegistration',
            })}
          </p>
        )}
        <Divider className="mt-4 bg-[#D6DEE4]" />
        {/* 자원등록 > 소속 발전소 관리 */}
        {progress === ResourceRegistrationProgress.AssociatedPlant && (
          <div className="mt-[14px] flex items-start gap-10">
            <div className="flex-1">
              <p className="noto-14-bold leading-[23px] text-dark-3">
                {t('management.modal.searchPowerPlant')} (209)
              </p>
              <div className="mt-4 flex items-center">
                <SearchInput
                  className="flex-1"
                  testId={`${testId}-search-input`}
                  placeholder="management.plantSearchInput"
                  handleChange={handleChange}
                  text={searchKeyword}
                />
              </div>
              <div className="mt-[9px] h-[387px] w-full min-w-[415px] overflow-scroll rounded-[5px] bg-light-2">
                {filteredData.map((data, index, array) => {
                  const lastIndex = array.length - 1;
                  return (
                    <Fragment key={index}>
                      <p
                        className="noto-normal-3 flex cursor-pointer items-center px-[19px] leading-[43px] text-dark-3"
                        onClick={() => handleSiteClick(data.siteId)}
                      >
                        {data.siteName}
                      </p>
                      {index !== lastIndex && (
                        <Divider className="bg-[#D6DEE4]" />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className="flex-1">
              <p className="noto-14-bold leading-[23px] text-dark-3">
                {t('management.modal.associatedPowerPlant')} (6)
              </p>
              <div className="mt-4 h-[432px] w-full min-w-[415px] overflow-scroll rounded-[5px] bg-light-2">
                {selectedData.map((data, index) => {
                  return (
                    <div
                      key={index}
                      className="m-[10px] mb-[5px] flex h-[42px] items-center justify-between rounded-[7px] bg-white"
                    >
                      <p className="noto-normal-3 px-[17px] leading-[23px] text-dark-3">
                        {data.siteName}
                      </p>
                      <SVGImage
                        onClick={() => handleSiteClick(data.siteId, true)}
                        width={12}
                        height={12}
                        className="mr-5 cursor-pointer"
                        name="close"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {progress === ResourceRegistrationProgress.ResourceRegistration && (
          <div className="pt-[36px]">
            <p className="noto-12-medium mb-[7px] leading-none text-dark-6">
              {t('management.modal.resourceSelector')}
            </p>
            <Dropdown
              className="w-full text-dark-6 [&>div:first-child>button]:noto-normal-3 [&>div:last-child>p]:pop-normal-3 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-light-2 [&>div:first-child>button]:py-[13px] [&>div:first-child>button]:pl-[9px] [&>div:first-child>button]:pr-[11px] [&>div:last-child]:ring-light-3"
              items={[
                t('management.modal.group'),
                t('management.modal.individual'),
              ]}
              selectedItem={t('addAccountModal.defaultRole')}
              onSelected={() => {}}
            />
            <p className="noto-12-medium mb-[7px] mt-[14px] leading-none text-dark-6">
              {t('management.modal.collectiveResourceId')}
            </p>
            <FilledInput
              className="h-[36px] w-full"
              text="자동생성된 집합자원 코드 노출"
              disabled={true}
            />
            <p className="noto-12-medium mb-[7px] mt-[14px] leading-none text-dark-6">
              {t('management.resourceName', { context: 'space' })}
            </p>
            <FilledInput className="h-[36px] w-full" />
            <p className="noto-12-medium mb-[7px] mt-[14px] leading-none text-dark-6">
              {t('management.resourceDescription', { context: 'space' })}
            </p>
            <FilledInput className="h-[36px] w-full" />
          </div>
        )}
        <Divider className="mt-[22px] bg-[#3F4C5A]" />
        <div className="mt-[30px] flex items-center">
          {progress === ResourceRegistrationProgress.ResourceRegistration && (
            <CancleButton
              onClick={() =>
                setProgress(ResourceRegistrationProgress.AssociatedPlant)
              }
              className="justify-between px-[30px] [&]:bg-dark-9"
              text={t('common.previous')}
              testId={`${testId}-cancle-button`}
              disabled={false}
              submitted={false}
            />
          )}
          <SubmitButton
            onClick={() =>
              setProgress(ResourceRegistrationProgress.ResourceRegistration)
            }
            className="ml-auto px-[30px] [&]:justify-between"
            text={t('management.modal.nextButton', { context: 'submit' })}
            testId={`${testId}-submit-button`}
            disabled={false}
            submitted={false}
          />
        </div>
      </div>
    </div>
  );
};
